import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { api, prospects as prospectsApi } from '../api/client.ts';
import { PROSPECT_STATUS_LABEL, STAGE_LABEL } from '../api/types.ts';
import type { FunnelSummary, Opportunity, OutreachPerformance, OutreachSplit, ProspectStage } from '../api/types.ts';
import { useAuth } from '../auth/AuthContext.tsx';
import { ArrowUpRight, ChevronDown } from 'lucide-react';
import { useAiSpend, useQueueSummary } from '../api/queries.ts';
import { Badge, EmptyState, PageHeader, Panel, ScoreValue, Segmented, Spinner, StageRail, cx, spotlight, table } from '../ui/primitives.tsx';
import { useDocumentTitle } from '../ui/Logo.tsx';
import { FollowUpsList } from './Discovery.tsx';

interface QueueStat {
  type: string;
  status: string;
  count: number;
}

interface UsageRow {
  provider: string;
  requests: number;
  errors: number;
  cost_usd: number;
}

interface Observability {
  queue: QueueStat[];
  usage: UsageRow[];
  failures: { id: string; type: string; attempts: number; last_error: string | null }[];
}

type Period = 30 | 90 | 0;

const RAIL: ProspectStage[] = ['a_contatar', 'aguardando', 'responderam', 'negociacao'];

/**
 * Painel inicial.
 *
 * A ordem é a do dia de quem vende: onde estão as empresas (o trilho), com
 * quem falar hoje (retomadas e melhores oportunidades), e só depois os
 * números. Fila e custo de API são o motor, não a venda — ficam numa seção
 * recolhida que abre sozinha quando algo falhou.
 *
 * Número inventado num painel é pior que espaço vazio: cada bloco diz o que
 * fazer quando ainda não há dado.
 */
export function Dashboard() {
  useDocumentTitle('Painel');
  const navigate = useNavigate();
  const { user, profiles } = useAuth();
  const [days, setDays] = useState<Period>(30);
  const periodQuery = days ? `?days=${days}` : '';

  const { data, isLoading, error } = useQuery({
    queryKey: ['observability'],
    queryFn: () => api<Observability>('/admin/observability'),
    refetchInterval: 30_000,
  });
  const summary = useQuery({ queryKey: ['prospects', 'summary', ''], queryFn: () => prospectsApi.summary() });
  const funnel = useQuery({
    queryKey: ['metrics', 'funnel', days],
    queryFn: () => api<FunnelSummary>(`/metrics/funnel${periodQuery}`),
  });
  const opportunities = useQuery({
    queryKey: ['metrics', 'opportunities'],
    queryFn: () => api<{ opportunities: Opportunity[] }>('/metrics/opportunities?limit=8'),
  });
  const outreach = useQuery({
    queryKey: ['metrics', 'outreach', days],
    queryFn: () => api<OutreachPerformance>(`/metrics/outreach${periodQuery}`),
  });

  const queue = useQueueSummary();
  const aiSpend = useAiSpend();
  const profile = profiles.find((item) => item.id === user?.activeProfileId);
  const failures = data?.failures.length ?? 0;
  const waiting = queue.data ? queue.data.drafts + queue.data.replies : undefined;

  return (
    <>
      <PageHeader
        title={`${greeting(user?.name)}.`}
        emphasis={waiting === undefined ? undefined : waiting === 0 ? 'Nada esperando você agora.' : waitingPhrase(queue.data!)}
        description={profile ? `Operação de ${profile.name}.` : undefined}
      />

      <section aria-label="Para hoje" className="mb-8 grid gap-3 sm:grid-cols-3">
        <TodayTile
          to="/fila"
          label="Abordagens para aprovar"
          value={queue.data?.drafts}
          hint="A IA redigiu; falta a sua decisão."
          highlight={Boolean(queue.data?.drafts)}
        />
        <TodayTile
          to="/fila?tab=respostas"
          label="Respostas para dar"
          value={queue.data?.replies}
          hint="Empresas que escreveram e esperam."
          highlight={Boolean(queue.data?.replies)}
        />
        <TodayTile
          label="Gasto de IA nas últimas 24h"
          value={aiSpend.data ? `US$ ${aiSpend.data.spent24h.toFixed(2).replace('.', ',')}` : undefined}
          hint={
            aiSpend.data
              ? `Teto de US$ ${aiSpend.data.dailyBudgetUsd.toFixed(2).replace('.', ',')} por dia, somando todos os fornecedores.`
              : undefined
          }
        />
      </section>

      <section aria-label="Etapas da venda" className="mb-8">
        <StageRail
          label="Etapas da venda"
          onSelect={(key) => navigate(key === 'a_contatar' ? '/prospects' : `/prospects?etapa=${key}`)}
          stages={RAIL.map((key) => ({ key, label: STAGE_LABEL[key], count: summary.data?.byStage[key] }))}
        />
      </section>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)]">
        {/* Primeiro o que é para fazer hoje; depois os números. */}
        <Panel title="Retomar contato" description="Datas combinadas com as empresas: de hoje e atrasadas.">
          <FollowUpsList />
        </Panel>
        <OpportunitiesPanel opportunities={opportunities.data?.opportunities} isLoading={opportunities.isLoading} />
      </div>

      <div className="mt-10 mb-4 flex flex-wrap items-end justify-between gap-3">
        <h2 className="text-lg font-semibold">Resultados</h2>
        <Segmented<Period>
          label="Período dos resultados"
          value={days}
          onChange={setDays}
          options={[
            { value: 30, label: '30 dias' },
            { value: 90, label: '90 dias' },
            { value: 0, label: 'Desde o início' },
          ]}
        />
      </div>
      <div className="grid gap-6">
        <FunnelPanel funnel={funnel.data} isLoading={funnel.isLoading} error={Boolean(funnel.error)} period={days} />
        <OutreachPanel performance={outreach.data} isLoading={outreach.isLoading} error={Boolean(outreach.error)} />
      </div>

      {/* Fila, custo e falhas: é o motor, não a venda. Fica recolhido, e abre
          sozinho quando tem job com falha para alguém olhar. */}
      <details
        className="group mt-10 rounded-[var(--radius-lg)] border border-[var(--color-line)] bg-[var(--color-surface)]"
        open={failures > 0}
      >
        <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-5 py-4">
          <span>
            <span className="font-display text-[15px] font-semibold">Sistema</span>
            <span className="ml-2 hidden text-sm text-[var(--color-ink-soft)] sm:inline">
              fila de processamento, consumo de APIs e falhas
            </span>
          </span>
          <span className="flex items-center gap-2">
            {failures > 0 && <Badge tone="danger">{failures} com falha</Badge>}
            <ChevronDown aria-hidden="true" className="size-4 text-[var(--color-ink-faint)] transition-transform group-open:rotate-180" />
          </span>
        </summary>
        <div className="grid gap-8 border-t border-[var(--color-line)] px-5 py-5 lg:grid-cols-2">
          <div className="min-w-0">
            <h3 className="mb-2 text-sm font-semibold">Fila de processamento</h3>
            {isLoading && <Spinner label="Carregando" />}
            {error && <p className="text-sm text-[var(--color-danger)]">Não foi possível carregar.</p>}
            {data && data.queue.length === 0 && <p className="text-sm text-[var(--color-ink-soft)]">Nada na fila agora.</p>}
            {data && data.queue.length > 0 && (
              <table className={table.table}>
                <thead>
                  <tr className="text-left text-xs text-[var(--color-ink-soft)]">
                    <th className="pb-2 font-medium">Tipo</th>
                    <th className="pb-2 font-medium">Status</th>
                    <th className="pb-2 text-right font-medium">Qtd.</th>
                  </tr>
                </thead>
                <tbody>
                  {data.queue.map((row) => (
                    <tr key={`${row.type}-${row.status}`} className="border-t border-[var(--color-line)]">
                      <td className="py-1.5" title={row.type}>{JOB_LABEL[row.type] ?? row.type}</td>
                      <td className="py-1.5 text-[var(--color-ink-soft)]">{JOB_STATUS_LABEL[row.status] ?? row.status}</td>
                      <td className="tabular py-1.5 text-right">{row.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          <div className="min-w-0">
            <h3 className="mb-2 text-sm font-semibold">Consumo de APIs, 7 dias</h3>
            {data && data.usage.length === 0 ? (
              <p className="text-sm text-[var(--color-ink-soft)]">
                Nenhuma chamada registrada. Toda requisição a IA, Apify e Evolution aparece aqui, inclusive as que falham.
              </p>
            ) : (
              <table className={table.table}>
                <thead>
                  <tr className="text-left text-xs text-[var(--color-ink-soft)]">
                    <th className="pb-2 font-medium">Provedor</th>
                    <th className="pb-2 text-right font-medium">Chamadas</th>
                    <th className="pb-2 text-right font-medium">Erros</th>
                    <th className="pb-2 text-right font-medium">Custo (US$)</th>
                  </tr>
                </thead>
                <tbody>
                  {data?.usage.map((row) => (
                    <tr key={row.provider} className="border-t border-[var(--color-line)]">
                      <td className="py-1.5">{row.provider}</td>
                      <td className="tabular py-1.5 text-right">{row.requests}</td>
                      <td className="tabular py-1.5 text-right">{row.errors}</td>
                      <td className="tabular py-1.5 text-right">{row.cost_usd.toFixed(4)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          {data && data.failures.length > 0 && (
            <div className="lg:col-span-2">
              <h3 className="mb-2 text-sm font-semibold">Jobs com falha</h3>
              <ul className="flex flex-col gap-2">
                {data.failures.map((job) => (
                  <li key={job.id} className="border-l-2 border-[var(--color-danger)] pl-3 text-sm">
                    <span className="font-medium" title={job.type}>{JOB_LABEL[job.type] ?? job.type}</span>{' '}
                    <span className="text-[var(--color-ink-faint)]">({job.attempts} tentativas)</span>
                    <p className="break-words text-[var(--color-ink-soft)]">{job.last_error}</p>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </details>
    </>
  );
}

/** O que cada job faz, nas palavras de quem usa o painel. O nome técnico fica no `title`. */
const JOB_LABEL: Record<string, string> = {
  'campaign.collect': 'Coleta da campanha',
  'prospect.enrich': 'Qualificação e leitura do site',
  'prospect.analyze': 'Análise com IA',
  'message.generate': 'Redação de mensagem',
  'message.send': 'Envio pelo WhatsApp',
};

const JOB_STATUS_LABEL: Record<string, string> = {
  pending: 'Na fila',
  running: 'Rodando',
  done: 'Concluído',
  failed: 'Falhou',
};

function waitingPhrase(summary: { drafts: number; replies: number }): string {
  const parts = [
    summary.drafts ? `${summary.drafts} ${summary.drafts === 1 ? 'abordagem' : 'abordagens'}` : '',
    summary.replies ? `${summary.replies} ${summary.replies === 1 ? 'resposta' : 'respostas'}` : '',
  ].filter(Boolean);
  const total = summary.drafts + summary.replies;
  return `${parts.join(' e ')} ${total === 1 ? 'espera' : 'esperam'} por você.`;
}

/**
 * Um número do dia, clicável quando leva a uma tela. Em destaque (a luz azul
 * da marca) só o que espera decisão de alguém — o resto é contexto.
 */
function TodayTile({
  to,
  label,
  value,
  hint,
  highlight = false,
}: {
  to?: string;
  label: string;
  value: number | string | undefined;
  hint?: string;
  highlight?: boolean;
}) {
  const body = (
    <>
      <span className="flex items-center justify-between gap-2 text-xs text-[var(--color-ink-soft)]">
        {label}
        {to && (
          <ArrowUpRight
            aria-hidden="true"
            className="size-3.5 text-[var(--color-ink-faint)] transition-colors group-hover:text-[var(--color-brand-text)]"
          />
        )}
      </span>
      <span className="font-display tabular text-[30px] leading-none font-semibold text-[var(--color-ink)]">{value ?? '–'}</span>
      {hint && <span className="hidden text-xs text-[var(--color-ink-faint)] sm:block">{hint}</span>}
    </>
  );
  const shape = cx(
    'spot group flex flex-col gap-2 overflow-hidden rounded-[var(--radius-lg)] border p-4 transition-colors sm:min-h-[120px] sm:gap-3',
    highlight
      ? 'border-[rgb(10_92_255/0.5)] bg-[linear-gradient(160deg,rgb(10_92_255/0.18),rgb(10_92_255/0.03)_60%)] shadow-[inset_0_1px_0_rgb(0_180_255/0.3)]'
      : 'border-[var(--color-line)] bg-[var(--color-surface)] shadow-[var(--shadow-panel)]',
    to && 'hover:border-[var(--color-line-strong)]',
  );
  return to ? (
    <Link to={to} className={shape} onMouseMove={spotlight}>
      {body}
    </Link>
  ) : (
    <div className={shape}>{body}</div>
  );
}

/** "Bom dia, Lucas" — pelo relógio de quem abre, com o primeiro nome. */
function greeting(name: string | undefined): string {
  const hour = new Date().getHours();
  const part = hour < 12 ? 'Bom dia' : hour < 18 ? 'Boa tarde' : 'Boa noite';
  const first = name?.trim().split(/\s+/)[0];
  return first ? `${part}, ${first}` : part;
}

/**
 * Cada etapa conta por um EVENTO gravado em `activities` (`message.sent`,
 * `whatsapp.reply_received`, mudança de status para reunião/proposta/venda),
 * não pelo status atual do prospect — quem virou `WON` continua contando como
 * "contatado" e "respondeu", porque de fato foi. Ver `repo/metrics.ts`.
 */
function FunnelPanel({
  funnel,
  isLoading,
  error,
  period,
}: {
  funnel: FunnelSummary | undefined;
  isLoading: boolean;
  error: boolean;
  period: Period;
}) {
  const empty = funnel?.stages.every((stage) => stage.count === 0);
  return (
    <Panel
      title="Funil"
      description={
        period
          ? `Últimos ${period} dias: cada etapa conta pela data em que a empresa chegou nela.`
          : 'Desde o início. Cada etapa conta quem chegou nela, mesmo depois de avançar.'
      }
    >
      {isLoading && <Spinner label="Carregando o funil" />}
      {error && <p className="text-sm text-[var(--color-danger)]">Não foi possível carregar o funil.</p>}
      {funnel && empty && (
        <EmptyState
          title={period ? 'Nada neste período' : 'Nenhum prospect ainda'}
          description={
            period
              ? 'Nenhuma empresa entrou ou avançou nesses dias. Troque o período para ver mais.'
              : 'O funil aparece assim que a primeira campanha trouxer empresas.'
          }
        />
      )}
      {funnel && !empty && (
        <div className="flex flex-col gap-6 lg:flex-row lg:gap-10">
          <div className="flex flex-1 flex-col gap-2.5">
            {funnel.stages.map((stage) => {
              const max = Math.max(...funnel.stages.map((item) => item.count), 1);
              const pct = Math.max((stage.count / max) * 100, stage.count > 0 ? 2 : 0);
              return (
                <div key={stage.key} className="flex items-center gap-3 text-sm">
                  <span className="w-24 shrink-0 text-[var(--color-ink-soft)]">{stage.label}</span>
                  <div className="h-2 flex-1 rounded-full bg-[rgb(255_255_255/0.06)]">
                    <div
                      className="h-2 rounded-full bg-[var(--color-brand)] shadow-[0_0_12px_rgb(10_92_255/0.55)]"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <span className="font-display tabular w-12 shrink-0 text-right font-semibold">{stage.count}</span>
                </div>
              );
            })}
          </div>
          <dl className="grid shrink-0 grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-3 lg:w-64 lg:grid-cols-2">
            {funnel.rates.map((rate) => (
              <div key={rate.key}>
                <dt className="text-xs text-[var(--color-ink-faint)]">{rate.label}</dt>
                <dd className="font-display tabular text-xl font-semibold">
                  {rate.value === null ? '—' : `${(rate.value * 100).toFixed(0)}%`}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      )}
    </Panel>
  );
}

/**
 * O que faz as pessoas responderem.
 *
 * Três cortes da MESMA taxa de resposta, e cada um responde a uma pergunta
 * diferente: o gancho diz com que tipo de observação abrir, a versão do prompt
 * diz se mexer no texto melhorou, e o tipo diz se o follow-up vale a pena.
 *
 * O painel não esconde amostra pequena — mostra "enviadas" ao lado da taxa,
 * porque 1 de 1 é 100% e não significa nada. Quem lê precisa ver o denominador.
 */
function OutreachPanel({
  performance,
  isLoading,
  error,
}: {
  performance: OutreachPerformance | undefined;
  isLoading: boolean;
  error: boolean;
}) {
  return (
    <Panel title="O que faz responder" description="Taxa de resposta por abertura, por versão do texto e por tipo de mensagem.">
      {isLoading && <Spinner label="Carregando" />}
      {error && <p className="text-sm text-[var(--color-danger)]">Não foi possível carregar.</p>}
      {performance && performance.total.sent === 0 && (
        <EmptyState
          title="Nenhuma mensagem enviada no período"
          description="Assim que as abordagens saírem, aparece aqui qual gancho traz resposta."
        />
      )}
      {performance && performance.total.sent > 0 && (
        <div className="flex flex-col gap-5">
          <p className="text-sm text-[var(--color-ink-soft)]">
            <span className="font-display tabular text-lg font-semibold text-[var(--color-ink)]">
              {performance.total.replied} de {performance.total.sent}
            </span>{' '}
            responderam
            {performance.total.rate !== null && ` (${(performance.total.rate * 100).toFixed(0)}%)`}.
          </p>
          <div className="grid gap-6 lg:grid-cols-3">
            <SplitList title="Por gancho" rows={performance.byHook} />
            <SplitList title="Por versão do texto" rows={performance.byPromptVersion} />
            <SplitList title="Por tipo" rows={performance.byKind} />
          </div>
        </div>
      )}
    </Panel>
  );
}

function SplitList({ title, rows }: { title: string; rows: OutreachSplit[] }) {
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <h3 className="text-xs font-medium text-[var(--color-ink-faint)]">{title}</h3>
      {rows.length === 0 && <p className="text-sm text-[var(--color-ink-soft)]">—</p>}
      {rows.map((row) => (
        <div key={row.key} className="flex items-baseline justify-between gap-2 text-sm">
          <span className="min-w-0 truncate text-[var(--color-ink-soft)]" title={row.label}>
            {row.label}
          </span>
          <span className="tabular shrink-0 font-medium">
            {row.rate === null ? '—' : `${(row.rate * 100).toFixed(0)}%`}
            <span className="text-xs font-normal text-[var(--color-ink-faint)]">
              {' '}
              ({row.replied}/{row.sent})
            </span>
          </span>
        </div>
      ))}
    </div>
  );
}

/**
 * As empresas para trabalhar hoje: nota alta e ainda em jogo (fora
 * descartadas, ganhas e perdidas). Reaproveita `final_score`, que já mede a
 * urgência — não é uma métrica nova, é a mesma nota da ficha, ordenada.
 */
function OpportunitiesPanel({
  opportunities,
  isLoading,
}: {
  opportunities: Opportunity[] | undefined;
  isLoading: boolean;
}) {
  return (
    <Panel title="Melhores oportunidades" description="Nota mais alta entre as empresas ainda em jogo.">
      {isLoading && <Spinner label="Carregando" />}
      {opportunities && opportunities.length === 0 && (
        <EmptyState
          title="Nenhuma oportunidade pontuada ainda"
          description="Aparece aqui assim que uma empresa for qualificada e pontuada."
        />
      )}
      {opportunities && opportunities.length > 0 && (
        <ul className="flex flex-col">
          {opportunities.map((item) => (
            <li key={item.id} className="border-t border-[var(--color-line)] first:border-t-0">
              <Link
                to={`/prospects/${item.id}`}
                className="-mx-2 flex items-center justify-between gap-3 rounded-[var(--radius-sm)] px-2 py-2 text-sm hover:bg-[var(--color-hover)]"
              >
                <span className="min-w-0 flex-1 truncate font-medium">
                  {item.name}
                  {item.city && <span className="font-normal text-[var(--color-ink-faint)]">, {item.city}</span>}
                </span>
                <span className="hidden shrink-0 text-xs text-[var(--color-ink-faint)] sm:inline">
                  {PROSPECT_STATUS_LABEL[item.status]}
                </span>
                <span className="w-8 shrink-0 text-right">
                  <ScoreValue value={item.final_score} />
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}
